// Constants and types for client/server contracts re: product analytics tracking

import { z } from "zod";
import { zAnyNonNullish } from "./zod/utils.js";

import {
  DataConnectionTypeLiteral,
  SqlCompletionTypeLiteral,
} from "./dataConnectionTypes.js";
import {
  CellType,
  DataBrowserTabLiteral,
  NotificationTypeLiteral,
  OrgRole,
  OrgRoleLiteral,
  ProjectRoleLiteral,
  ViewDataOpenedFromLiteral,
} from "./enums.js";
import { AppFeatureFlagRuntype } from "./FeatureFlag.js";
import {
  FeatureGateClickedEventData,
  FeatureGateTierLiteral,
} from "./FeatureGate.js";
import {
  AgentChatThreadId,
  CellId,
  CollectionId,
  ContextSuggestionIssueId,
  DataConnectionId,
  DataSourceTableId,
  HexId,
  HexVersionId,
  MagicEventId,
  NotificationUserLinkId,
  SemanticDatasetId,
  SemanticProjectId,
  SemanticViewId,
  UserId,
} from "./idTypeBrands.js";
import { AgentChatThreadTypeLiteral } from "./magic/agent/threads.js";
import { ProjectTemplateLiteral } from "./projectTemplate.js";
import { getNormalEnum } from "./pseudoEnums.js";
import { NoCodeCellTypeLiteral } from "./sql/dataSourceTableConfig.js";
import { Requiredish } from "./utils/typeUtils.js";
import {
  ActionTakenLiteral,
  OnboardingFlowLiteral,
  OnboardingFlowStepLiteral,
} from "./workspaceSetupTypes.js";

/**
 * Amplitude event types that can be tracked via POST request to /track-event
 * Variable names should match the event_type defined in the Amplitude schema
 * The /track-event endpoint will reject any events not defined here
 * and explicitly handled in the server file
 */
export const ClientAnalyticsEventTypeLiteral = z.enum([
  "FEATURE_GATE_CLICKED",
  "DATA_CONNECTION_CREATION_FAILED",
  "DATA_CONNECTION_FORM_OPENED",
  "AUTOCOMPLETE_ACCEPTED",
  "AUTOCOMPLETE_TRIGGERED",
  "PROJECT_EMPTY_STATE_CELL_CLICKED",
  "FEATURE_FLAG_EVALUATED",
  "ONBOARDING_STEP_ADVANCED",
  "VIEW_DATA_OPENED",
  "PROJECT_TEMPLATE_PREVIEWED",
  "DEBUG_MODE_OPENED",
  "NOTIFICATION_INBOX_OPENED",
  "NOTIFICATION_CLICKED",
  "PRESENTATION_MODE_OPENED",
  "KEYBOARD_SHORTCUT_USED",
  "DATA_BROWSER_DIALOG_OPENED",
  "DATA_BROWSER_TABLE_SELECTED",
  "PUBLISH_MODAL_OPENED",
  "CLIENT_COUNTER_EVENT",
  "PROJECT_LIST_LAYOUT_UPDATED",
  "PROJECT_LIST_SORT_UPDATED",
  "PROJECT_LIST_FILTER_UPDATED",
  "EXPLORE_CREATED",
  "EXPLORE_ERRORED",
  "SEMANTIC_DATASET_SELECTED",
  "CTA_CLICKED",
  "FEATURE_GATE_CONTACT_SALES_SUBMITTED",
  "CTA_CONTACT_SALES_SUBMITTED",
  "CANCELLATION_STEP_ADVANCED",
  "SEMANTIC_WORKBENCH_PUBLISH_TAB_CLICKED",
  "DRILLED_INTO_EXPLORE",
  "SEMANTIC_VIEW_SELECTED",
  "THREAD_VIEWED",
  "THREAD_INSPECTOR_CLICK",
  "WORKSPACE_SEARCH_RESULT_CLICKED",
  "DATA_BROWSER_SEARCH_TRIGGERED",
  "DATA_BROWSER_SEARCH_RESULT_SELECTED",
  "CONTEXT_STUDIO_THREAD_OPENED",
  "CONTEXT_STUDIO_FILTER_CHANGED",
  "CONTEXT_STUDIO_VIEWED",
  "CONTEXT_STUDIO_SUGGESTION_UI_ACTION",
  "EXECUTION_PROFILER_OPENED",
  "EXECUTION_PROFILER_TRACE_ID_COPIED",
  "AGENT_MODEL_EFFORT_SELECTED",
  "DATA_APP_SELECT_NUDGE",
  "CHECKOUT_STEP_VIEWED",
  "CHECKOUT_PAYMENT_SUBMITTED",
  "CHECKOUT_DIALOG_DISMISSED",
  "CHECKOUT_SEAT_CHANGED",
]);
export type ClientAnalyticsEventType = z.infer<
  typeof ClientAnalyticsEventTypeLiteral
>;
export const ClientAnalyticsEventType = getNormalEnum(
  ClientAnalyticsEventTypeLiteral,
);

const DataBrowserDialogOpenedSourceLiteral = z.enum([
  "PROJECT_EMPTY_STATE",
  "EXPLORE_EMPTY_STATE",
  "EXPLORE_JOIN_DATA",
  "ACTION_BAR",
  "ADD_CELL_MENU",
  "SIDEBAR",
  "SIDEBAR_PREVIEW",
  "UNKNOWN",
  "HOME_DATA_TAB",
  "MAGIC_FEEDBACK",
  "MAGIC_MENTION",
  "LOGIC_VIEW_PROMPT_BAR",
]);

export const DataBrowserDialogOpenedSource = {
  ...getNormalEnum(DataBrowserDialogOpenedSourceLiteral),
  ...getNormalEnum(NoCodeCellTypeLiteral),
};
export type DataBrowserDialogOpenedSource = z.infer<
  typeof DataBrowserDialogOpenedSourceLiteral | typeof NoCodeCellTypeLiteral
>;

export const DataBrowserDialogOpenedEventData = z.object({
  dataBrowserOpenedSource: DataBrowserDialogOpenedSourceLiteral.or(
    NoCodeCellTypeLiteral,
  ),
});
type DataBrowserDialogOpenedEventData = z.infer<
  typeof DataBrowserDialogOpenedEventData
>;

export const DataBrowserTypeLiteral = z.enum([
  "PROJECT_DIALOG",
  "PROJECT_SIDEBAR",
  "HOME_DATA_TAB",
  "HOME_DATA_BENTO",
  "HOME_SIDEBAR",
  "PROJECT_EMPTY_STATE",
  "EXPLORE_EMPTY_STATE",
  "THREAD_AGENT_TOOL_RESULT",
]);

export type DataBrowserType = z.infer<typeof DataBrowserTypeLiteral>;
export const DataBrowserType = getNormalEnum(DataBrowserTypeLiteral);

const TableActionTakenLiteral = NoCodeCellTypeLiteral.or(
  z.literal(CellType.WRITEBACK),
)

  .or(z.literal(CellType.SQL))

  .or(z.literal("SELECT"))
  .or(z.literal("EXPLORE_UI"));

export const DataBrowserTableSelectedEventData = z.object({
  browserType: DataBrowserTypeLiteral,
  dataSourceTableId: DataSourceTableId.or(z.null()),
  dataConnectionId: DataConnectionId.or(z.null()),

  tableActionTaken: TableActionTakenLiteral,
  dataBrowserTab: DataBrowserTabLiteral,
});
export type DataBrowserTableSelectedEventData = z.infer<
  typeof DataBrowserTableSelectedEventData
>;

export const SemanticDatasetSelectedEventData = z.object({
  tableActionTaken: TableActionTakenLiteral,
  dataBrowserTab: DataBrowserTabLiteral,
  browserType: DataBrowserTypeLiteral,
  semanticDatasetId: SemanticDatasetId,
  semanticProjectId: SemanticProjectId,
});

export type SemanticDatasetSelectedEventData = z.infer<
  typeof SemanticDatasetSelectedEventData
>;

export const SemanticViewSelectedEventData = z.object({
  tableActionTaken: TableActionTakenLiteral,
  dataBrowserTab: DataBrowserTabLiteral,
  browserType: DataBrowserTypeLiteral,
  semanticViewId: SemanticViewId,
  semanticProjectId: SemanticProjectId,
});

export type SemanticViewSelectedEventData = z.infer<
  typeof SemanticViewSelectedEventData
>;

export const ThreadViewedEventData = z.object({
  threadId: AgentChatThreadId,
  threadType: AgentChatThreadTypeLiteral,
});
export type ThreadViewedEventData = z.infer<typeof ThreadViewedEventData>;

export const ThreadInspectorClickItemClickedLiteral = z.enum([
  "OPEN",
  "CLOSE",
  "TIMELINE_TAB",
  "WARNINGS_TAB",
  "CONTEXT_STUDIO_LINK",
]);
export type ThreadInspectorClickItemClicked = z.infer<
  typeof ThreadInspectorClickItemClickedLiteral
>;
export const ThreadInspectorClickItemClicked = getNormalEnum(
  ThreadInspectorClickItemClickedLiteral,
);

export const ThreadInspectorClickEventData = z.object({
  itemClicked: ThreadInspectorClickItemClickedLiteral,
  threadId: AgentChatThreadId.optional(),
});
export type ThreadInspectorClickEventData = z.infer<
  typeof ThreadInspectorClickEventData
>;

const ProjectEmptyStateCellClickedTriggeredFromLiteral = z.enum([
  "FEATURED",
  "ADD_CELL_MENU",
  "WORKSPACE_DATA_CONNECTION",
  "FILE_UPLOAD",
]);

export const ProjectEmptyStateCellClickedTriggeredFrom = getNormalEnum(
  ProjectEmptyStateCellClickedTriggeredFromLiteral,
);

export type ProjectEmptyStateCellClickedTriggeredFrom = z.infer<
  typeof ProjectEmptyStateCellClickedTriggeredFromLiteral
>;
export const ProjectEmptyStateCellClickedEventData = z.object({
  cellType: z.string(),
  projectId: HexId,
  triggeredFrom: ProjectEmptyStateCellClickedTriggeredFromLiteral,
  orgRole: OrgRoleLiteral.optional(),
});

export type ProjectEmptyStateCellClickedEventData = z.infer<
  typeof ProjectEmptyStateCellClickedEventData
>;

// used for the DataConnectionCreationFailed event and the DataConnectionFormOpened event
export const DataConnectionCreationEventData = z.object({
  connectionType: DataConnectionTypeLiteral,
  projectVersionId: HexVersionId.optional(),
  orgRole: OrgRoleLiteral.optional(),
});
type DataConnectionCreationEventData = z.infer<
  typeof DataConnectionCreationEventData
>;

export const DataBrowserSearchTriggeredEventData = zAnyNonNullish();
export type DataBrowserSearchTriggeredEventData = z.infer<
  typeof DataBrowserSearchTriggeredEventData
>;
export const DataBrowserSearchResultSelectedEventData = z.object({
  resultType: z.string(),
  networkRank: z.number().or(z.null()).optional(),
  uiRank: z.number().or(z.null()).optional(),
});
export type DataBrowserSearchResultSelectedEventData = z.infer<
  typeof DataBrowserSearchResultSelectedEventData
>;

export const WorkspaceSearchResultTypeLiteral = z.enum([
  "LOGIC",
  "APP",
  "LOGIC-CELL",
]);
export type WorkspaceSearchResultType = z.infer<
  typeof WorkspaceSearchResultTypeLiteral
>;
export const WorkspaceSearchResultType = getNormalEnum(
  WorkspaceSearchResultTypeLiteral,
);

export const WorkspaceSearchResultClickedEventData = z.object({
  rank: z.number(),
  searchTraceId: z.string().optional(),
  resultType: WorkspaceSearchResultTypeLiteral,
  projectId: HexId.optional(),
});
type WorkspaceSearchResultClickedEventData = z.infer<
  typeof WorkspaceSearchResultClickedEventData
>;
export const SqlAutocompleteAcceptedEventData = z.object({
  autocompleteProviderType: z.literal("SQL"),
  networkRank: z.number().optional(),
  suggestionType: SqlCompletionTypeLiteral,
});
type SqlAutocompleteAcceptedEventData = z.infer<
  typeof SqlAutocompleteAcceptedEventData
>;
export const SqlAutocompleteTriggeredEventData = z.object({
  autocompleteProviderType: z.literal("SQL"),
});
type SqlAutocompleteTriggeredEventData = z.infer<
  typeof SqlAutocompleteTriggeredEventData
>;

export const KeyboardShortcutUsedEventData = z.object({
  key: z.string(),
  action: z.string(),
});
type KeyboardShortcutUsedEventData = z.infer<
  typeof KeyboardShortcutUsedEventData
>;

export const FlagEvaluationTypeLiteral = z.enum(["ENV", "ORG", "USER"]);

export const FlagEvaluationType = getNormalEnum(FlagEvaluationTypeLiteral);

export const FeatureFlagEvaluatedEventData = z.object({
  orgRole: OrgRoleLiteral.optional(),
  variation: z.string(),
  featureFlagName: AppFeatureFlagRuntype,
});
type FeatureFlagEvaluatedEventData = z.infer<
  typeof FeatureFlagEvaluatedEventData
>;

export const NotificationInboxOpenedEventData = z.object({
  numberUnreadNotifications: z.number(),
});
type NotificationInboxOpenedEventData = z.infer<
  typeof NotificationInboxOpenedEventData
>;

export const NotificationClickedEventData = z.object({
  notificationId: NotificationUserLinkId,
  notificationType: NotificationTypeLiteral,
});
type NotificationClickedEventData = z.infer<
  typeof NotificationClickedEventData
>;

export const PublishModalOpenedEventData = z.object({
  projectId: HexId,
  projectVersionId: HexVersionId,
  projectRole: ProjectRoleLiteral.optional(),
});
type PublishModalOpenedEventData = z.infer<typeof PublishModalOpenedEventData>;

/**
 * Constants and utils for identifying anonymous users in 3rd party
 * analytic services such as Rudderstack and LaunchDarkly.
 * These services typically charge by MAU, where each anonymous user
 * would count as a new MAU - so we want to treat anonymous users for those
 * services as a single user.
 * See https://docs.launchdarkly.com/home/users/anonymous-users
 */
export const ANALYTICS_ANONYMOUS_USER = {
  id: "bb92b229-9d51-491d-a679-9a0734f1e21c",
  email: "anonymous@example.com",
  name: "anonymous",
  stackId: "anonymous",
  orgRole: OrgRole.ANONYMOUS,
};

export const TriggeredFromLiteral = z.enum([
  "WORKSPACE_SETUP",
  "SETUP_CHECKLIST",
  "WORKSPACE_SETTINGS",
  "CONTEXT_SETUP",
  "AGGREGATE_BILLABLE_USAGE_JOB",
]);

export type TriggeredFromType = z.infer<typeof TriggeredFromLiteral>;

export const TriggeredFromType = getNormalEnum(TriggeredFromLiteral);

export const PublishedAppViewInAppReferrerLiteral = z.enum([
  "WORKSPACE_SEARCH",
  "RECENTLY_VIEWED_BENTO",
  "POPULAR_THIS_WEEK_BENTO",
  "RECENTLY_PUBLISHED_BENTO",
  "ALL_PROJECTS",
  "FAVORITES",
  "COLLECTION",
  "DEFLECTION",
  "SEMANTIC_PROJECT_WORKBENCH",
]);
export type PublishedAppViewInAppReferrer = z.infer<
  typeof PublishedAppViewInAppReferrerLiteral
>;
export const PublishedAppViewInAppReferrer = getNormalEnum(
  PublishedAppViewInAppReferrerLiteral,
);

const ProjectFileUploadTriggeredFromLiteral = z.enum([
  "PROJECT_EMPTY_STATE",
  "PROJECT_SIDEBAR",
  "CELL_EMBED",
  "PROJECT_FILE_UPLOAD_CELL",
  "PROJECT_ONBOARDING",
  "AI_PROMPT_ONBOARDING",
  "AI_PROMPT_ONBOARDING_DEMO_CSV",
  "NOTEBOOK_AGENT_PROMPT_BAR",
  "THREAD_PROMPT_BAR",
  "QUICK_SEARCH",
  "THREAD_PREVIEW",
]);

export const ProjectTemplatePreviewedEventData = z.object({
  projectHasCells: z.boolean(),
  templateName: ProjectTemplateLiteral,
  orgRole: OrgRoleLiteral.optional(),
});
export type ProjectTemplatePreviewedEventData = z.infer<
  typeof ProjectTemplatePreviewedEventData
>;

export type ProjectFileUploadTriggeredFrom = z.infer<
  typeof ProjectFileUploadTriggeredFromLiteral
>;
export const ProjectFileUploadTriggeredFrom = getNormalEnum(
  ProjectFileUploadTriggeredFromLiteral,
);

export const OnboardingStepAdvancedEventData = z.object({
  actionTaken: ActionTakenLiteral,
  flow: OnboardingFlowLiteral,
  step: OnboardingFlowStepLiteral,
  orgRole: OrgRoleLiteral.optional(),
  properties: z.object({
    demoPrompt: z.string().optional(),
    dataConnectionType: DataConnectionTypeLiteral.optional(),
    uploadedFiles: z.number().optional(),
    onlyHasDemoConnection: z.boolean().optional(),
  }),
});
export type OnboardingStepAdvancedEventData = z.infer<
  typeof OnboardingStepAdvancedEventData
>;

export const ViewDataOpenedEventData = z.object({
  projectId: HexId,
  cellType: z.string(),
  triggeredFrom: ViewDataOpenedFromLiteral,
});
export type ViewDataOpenedEventData = z.infer<typeof ViewDataOpenedEventData>;

export const DebugModeOpenedEventData = z.object({
  projectId: HexId,
});
export type DebugModeOpenedEventData = z.infer<typeof DebugModeOpenedEventData>;

export const PresentationModeOpenedEventData = z.object({
  projectId: HexId,
});
export type PresentationModeOpenedEventData = z.infer<
  typeof PresentationModeOpenedEventData
>;

export const ClientCounterEventData = z.object({
  eventType: z.string(),
});
export type ClientCounterEventData = z.infer<typeof ClientCounterEventData>;

export const ProjectsPage = z.enum([
  "PROJECTS",
  "COLLECTION",
  "COMPONENTS",
  "ARCHIVE",
  "TRASH",
  "LIBRARY",
  "EXPLORATIONS",
  "SEMANTIC_PROJECT_WORKBENCH",
  "ENDORSEMENTS",
]);
export type ProjectsPage = z.infer<typeof ProjectsPage>;

export const ProjectListLayoutUpdatedEventData = z.object({
  layout: z.enum(["CARD", "LIST"]),
  collectionId: CollectionId.optional(),
  page: ProjectsPage,
  orgRole: OrgRoleLiteral.optional(),
});
export type ProjectListLayoutUpdatedEventData = z.infer<
  typeof ProjectListLayoutUpdatedEventData
>;

export const ProjectListSortUpdatedEventData = z.object({
  layout: z.enum(["CARD", "LIST"]),
  direction: z.enum(["ASC", "DESC"]).optional(),
  sortField: z.string().optional(),
  triggeredFrom: z.enum(["TABLE_HEADER", "SORT_DROPDOWN"]),
  orgRole: OrgRoleLiteral.optional(),
  updateType: z.enum(["SORT_FIELD", "DIRECTION"]),
});
export type ProjectListSortUpdatedEventData = z.infer<
  typeof ProjectListSortUpdatedEventData
>;

export const ProjectListFilterUpdatedEventData = z.object({
  layout: z.enum(["CARD", "LIST"]),
  orgRole: OrgRoleLiteral.optional(),
  /**
   * The filter type that is being updated, e.g. "owner", "creator", "workspace", "public", "my-access".
   */
  updateType: z.string(),
  /**
   * The filter value for the updated filter type.
   */
  updateValue: z.union([z.string(), z.boolean(), z.null()]),
  /**
   * All active filter types at the time that updateType was updated.
   */
  activeFilters: z.array(z.string()),
});
export type ProjectListFilterUpdatedEventData = z.infer<
  typeof ProjectListFilterUpdatedEventData
>;

export const ExploreCreatedEventData = z.object({
  projectId: HexId,
  projectVersionId: HexVersionId,
  magicEventId: MagicEventId,
  // just being used for magic for now but can be extended in the future
  exploreSource: z.enum([
    "ASK_MAGIC_HOMEPAGE_USER_PROMPT",
    "ASK_MAGIC_HOMEPAGE_SUGGESTED_PROMPT",
    "ASK_MAGIC_EXPLORE_EMPTY_STATE_USER_PROMPT",
    "ASK_MAGIC_EXPLORE_EMPTY_STATE_SUGGESTED_PROMPT",
    "ASK_MAGIC_EXPLORE_TABLE_SELECTED_USER_PROMPT",
    "ASK_MAGIC_EXPLORE_TABLE_SELECTED_SUGGESTED_PROMPT",
  ]),
  exploreDatasourceType: z.enum(["DATAFRAME", "SEMANTIC", "DATAWAREHOUSE"]),
  hydrated: z.boolean().optional(),
});

export type ExploreCreatedEventData = z.infer<typeof ExploreCreatedEventData>;

export const ExploreErroredEventData = z.object({
  hasMagicEvent: z.boolean(),
  inputType: z.enum(["TABLE", "DATAFRAME", "SEMANTIC_DATASET"]),
  recognizedErrorType: z.string().optional(),
  orgRole: OrgRoleLiteral.optional(),
  outputId: z.string(),
  cellId: CellId,
});
export type ExploreErroredEventData = z.infer<typeof ExploreErroredEventData>;

const CheckoutStepLiteral = z.enum([
  "manage_plan",
  "choose_plan",
  "review_seats",
  "payment",
]);

const CheckoutOutcomeLiteral = z.enum(["success", "failure"]);

const CheckoutFailureReasonLiteral = z.enum([
  "STRIPE_VALIDATION",
  "PAYMENT_METHOD_MISSING",
  "SUBSCRIPTION_ERROR",
  "UNEXPECTED_ERROR",
]);

// Which checkout surface a funnel event came from — legacy /checkout route vs the
// new in-app dialog (gated by checkout-redesign feature flag)
const CheckoutVersionLiteral = z.enum(["legacy_route", "dialog"]);

const CheckoutSeatChangeTypeLiteral = z.enum([
  "ROLE_CHANGED",
  "REMOVED",
  "RESTORED",
]);

export const CtaClickedEventData = z.object({
  cta: z.enum([
    "CONTACT_SALES",
    "VIEW_PLANS",
    "CHOOSE_PROFESSIONAL",
    "CHOOSE_TEAM",
    "CHOOSE_COMMUNITY",
    "CHECKOUT_TEAM",
    "VIEW_BILLING",
  ]),
  triggeredFrom: z.enum([
    "TRIAL_BANNER",
    // Represents the OrgTiersView from the settings route
    "PRICING_PAGE",
    // Represents the OrgTiersViewV2 dialog
    "PLANS_MODAL",
    "COMMUNITY_UPGRADE_BANNER",
    "AI_PROMPT_LIMIT_CONTAINER",
    "TRIAL_END_MODAL",
    "AI_CREDIT_LIMIT_BANNER",
  ]),
});
export type CtaClickedEventData = z.infer<typeof CtaClickedEventData>;

export const CheckoutStepViewedEventData = z.object({
  step: CheckoutStepLiteral,
  tier: FeatureGateTierLiteral.optional(),
  version: CheckoutVersionLiteral.optional(),
  checkoutSessionId: z.string().optional(),
});
export type CheckoutStepViewedEventData = z.infer<
  typeof CheckoutStepViewedEventData
>;

export const CheckoutPaymentSubmittedEventData = z.object({
  outcome: CheckoutOutcomeLiteral,
  failure_reason: CheckoutFailureReasonLiteral.optional(),
  tier: FeatureGateTierLiteral.optional(),
  version: CheckoutVersionLiteral.optional(),
  checkoutSessionId: z.string().optional(),
});
export type CheckoutPaymentSubmittedEventData = z.infer<
  typeof CheckoutPaymentSubmittedEventData
>;

export const CheckoutDialogDismissedEventData = z.object({
  step: CheckoutStepLiteral,
  tier: FeatureGateTierLiteral.optional(),
  version: CheckoutVersionLiteral.optional(),
  checkoutSessionId: z.string().optional(),
});
export type CheckoutDialogDismissedEventData = z.infer<
  typeof CheckoutDialogDismissedEventData
>;

/**
 * A single seat edit in the review-seats step of the checkout process
 */
export const CheckoutSeatChangedEventData = z.object({
  change_type: CheckoutSeatChangeTypeLiteral,
  // The user whose seat was edited (not the acting admin).
  updatedUserId: UserId,
  // The role assigned on a ROLE_CHANGED edit (omitted for remove/restore).
  orgRole: z.string().optional(),
  tier: FeatureGateTierLiteral.optional(),
  version: CheckoutVersionLiteral.optional(),
  checkoutSessionId: z.string().optional(),
});
export type CheckoutSeatChangedEventData = z.infer<
  typeof CheckoutSeatChangedEventData
>;

export const SemanticWorkbenchPublishTabClickedEventData = z.object({
  semanticProjectId: SemanticProjectId,
  semanticWorkbenchPublishTab: z.enum([
    "CHANGES",
    "REFERENCES",
    "PREVIEW",
    "ASK_PREVIEW",
  ]),
});
export type SemanticWorkbenchPublishTabClickedEventData = z.infer<
  typeof SemanticWorkbenchPublishTabClickedEventData
>;

export const DrilledIntoExploreEventData = z.object({
  drill_context: z.enum(["APP", "EXPLORATION", "EXPLORE_CELL"]),
  drill_entry_point: z.enum(["CHART", "PIVOT", "NESTED_TABLE"]),
  drill_field_type: z.enum(["STRING", "NUMERIC", "BOOLEAN", "DATETIME"]),
});
export type DrilledIntoExploreEventData = z.infer<
  typeof DrilledIntoExploreEventData
>;

export const FeatureGateContactSalesSubmittedEventData = z.object({
  campaign: z.string(),
  featureGateDescription: z.string(),
  orgRole: OrgRoleLiteral.optional(),
  message: z.string().optional(),
});
export type FeatureGateContactSalesSubmittedEventData = z.infer<
  typeof FeatureGateContactSalesSubmittedEventData
>;

export const CtaContactSalesSubmittedEventData = z.object({
  campaign: z.string(),
  orgRole: OrgRoleLiteral.optional(),
  message: z.string().optional(),
});
export type CtaContactSalesSubmittedEventData = z.infer<
  typeof CtaContactSalesSubmittedEventData
>;

export const CancellationStepAdvancedEventData = z.object({
  cancellationFlow: z.string(),
  cancellationStep: z.string(),
  actionTaken: z.string(),
  cancellationReason: z.string().optional(),
  cancellationReasonOtherDetail: z.string().optional(),
});
export type CancellationStepAdvancedEventData = z.infer<
  typeof CancellationStepAdvancedEventData
>;

export const ContextStudioThreadOpenedEventData = z.object({
  threadId: AgentChatThreadId,
  threadType: AgentChatThreadTypeLiteral,
  threadOpenedFrom: z.enum(["MAIN_TABLE"]),
});
export type ContextStudioThreadOpenedEventData = z.infer<
  typeof ContextStudioThreadOpenedEventData
>;

export const ContextStudioFilterChangedEventData = z.object({
  filterType: z.enum([
    "AGENT_TYPE",
    "ORG_ROLE",
    "USER",
    "TOPIC",
    "RESOURCE",
    "MODEL",
    "TIME_PERIOD",
    "AGENT_CHAT_MESSAGE_SOURCE",
    "FEEDBACK",
    "WARNING_TYPE",
    "CLEAR_ALL",
  ]),
});
export type ContextStudioFilterChangedEventData = z.infer<
  typeof ContextStudioFilterChangedEventData
>;

const ContextStudioPageLiteral = z.enum([
  "DASHBOARD",
  "CONTEXT_SOURCES",
  "GUIDES",
  "ENDORSEMENTS",
  "MODELS",
  "REPOSITORIES",
  "SETUP",
  "SUGGESTIONS",
  "SUGGESTION_SUMMARY",
  "SUGGESTION_CHANGES",
  "MODEL_REFERENCES",
  "APPS",
  "EVALS",
  "KNOWLEDGE",
]);

export const ContextStudioViewedEventData = z.object({
  contextStudioPage: ContextStudioPageLiteral,
});
export type ContextStudioViewedEventData = z.infer<
  typeof ContextStudioViewedEventData
>;

export const ContextStudioSuggestionUiActionTypeLiteral = z.enum([
  "SUGGESTION_LIST_ITEM_CLICKED",
  "CLI_COMMAND_COPIED",
  "EVIDENCE_THREAD_LINK_CLICKED",
  "EVIDENCE_WARNING_EXPANDED",
  "THREAD_INSPECTOR_SUGGESTION_LINK_CLICKED",
]);
export type ContextStudioSuggestionUiActionType = z.infer<
  typeof ContextStudioSuggestionUiActionTypeLiteral
>;
export const ContextStudioSuggestionUiActionType = getNormalEnum(
  ContextStudioSuggestionUiActionTypeLiteral,
);

export const ContextStudioSuggestionUiActionEventData = z.object({
  actionType: ContextStudioSuggestionUiActionTypeLiteral,
  suggestionId: ContextSuggestionIssueId.optional(),
  threadId: AgentChatThreadId.optional(),
  triggeredFrom: z.enum(["HOME_PAGE", "SUGGESTIONS_PAGE"]).optional(),
});
export type ContextStudioSuggestionUiActionEventData = z.infer<
  typeof ContextStudioSuggestionUiActionEventData
>;

const ExecutionProfilerTriggeredFromLiteral = z.enum([
  "HELP_MENU",
  "CELL_METADATA",
  "PUBLISHED_APP",
]);

export const ExecutionProfilerOpenedEventData = z.object({
  projectId: z.string().optional(),
  triggeredFrom: ExecutionProfilerTriggeredFromLiteral,
});
export type ExecutionProfilerOpenedEventData = z.infer<
  typeof ExecutionProfilerOpenedEventData
>;

export const ExecutionProfilerTraceIdCopiedEventData = z.object({
  projectId: z.string().optional(),
});
export type ExecutionProfilerTraceIdCopiedEventData = z.infer<
  typeof ExecutionProfilerTraceIdCopiedEventData
>;

export const AgentModelEffortSelectedEventData = z.object({
  selectionType: z.enum(["MODEL", "EFFORT"]),
  fromModel: z.string(),
  toModel: z.string(),
  fromEffort: z.string(),
  toEffort: z.string(),
  modelChanged: z.boolean(),
  effortChanged: z.boolean(),
});
export type AgentModelEffortSelectedEventData = z.infer<
  typeof AgentModelEffortSelectedEventData
>;

/** The gen-app composer banner that points an edit-shaped draft at Select mode. */
export const DataAppSelectNudgeEventData = z.object({
  action: z.enum(["shown", "accept", "snooze", "dismiss"]),
  variant: z.enum(["chart", "text", "upgrade"]),
  agentChatThreadId: z.string().or(z.null()),
  hexVersionId: z.string(),
});
export type DataAppSelectNudgeEventData = z.infer<
  typeof DataAppSelectNudgeEventData
>;

export type ClientAnalyticsEventDataType =
  | FeatureGateClickedEventData
  | DataConnectionCreationEventData
  | SqlAutocompleteAcceptedEventData
  | SqlAutocompleteTriggeredEventData
  | ProjectEmptyStateCellClickedEventData
  | FeatureFlagEvaluatedEventData
  | OnboardingStepAdvancedEventData
  | ViewDataOpenedEventData
  | ProjectTemplatePreviewedEventData
  | DebugModeOpenedEventData
  | NotificationInboxOpenedEventData
  | NotificationClickedEventData
  | KeyboardShortcutUsedEventData
  | PresentationModeOpenedEventData
  | DataBrowserDialogOpenedEventData
  | DataBrowserTableSelectedEventData
  | PublishModalOpenedEventData
  | ClientCounterEventData
  | ProjectListLayoutUpdatedEventData
  | ProjectListSortUpdatedEventData
  | ProjectListFilterUpdatedEventData
  | ExploreCreatedEventData
  | ExploreErroredEventData
  | SemanticDatasetSelectedEventData
  | CtaClickedEventData
  | CheckoutStepViewedEventData
  | CheckoutPaymentSubmittedEventData
  | CheckoutDialogDismissedEventData
  | CheckoutSeatChangedEventData
  | CancellationStepAdvancedEventData
  | SemanticViewSelectedEventData
  | ThreadViewedEventData
  | ThreadInspectorClickEventData
  | ContextStudioThreadOpenedEventData
  | ContextStudioFilterChangedEventData
  | ContextStudioViewedEventData
  | ContextStudioSuggestionUiActionEventData
  | ExecutionProfilerOpenedEventData
  | ExecutionProfilerTraceIdCopiedEventData
  | AgentModelEffortSelectedEventData;

const _eventTypeToData = {
  [ClientAnalyticsEventType.FEATURE_GATE_CLICKED]: FeatureGateClickedEventData,
  [ClientAnalyticsEventType.DATA_CONNECTION_CREATION_FAILED]:
    DataConnectionCreationEventData,
  [ClientAnalyticsEventType.DATA_CONNECTION_FORM_OPENED]:
    DataConnectionCreationEventData,
  [ClientAnalyticsEventType.AUTOCOMPLETE_ACCEPTED]:
    SqlAutocompleteAcceptedEventData,
  [ClientAnalyticsEventType.AUTOCOMPLETE_TRIGGERED]:
    SqlAutocompleteTriggeredEventData,
  [ClientAnalyticsEventType.PROJECT_EMPTY_STATE_CELL_CLICKED]:
    ProjectEmptyStateCellClickedEventData,
  [ClientAnalyticsEventType.FEATURE_FLAG_EVALUATED]:
    FeatureFlagEvaluatedEventData,
  [ClientAnalyticsEventType.ONBOARDING_STEP_ADVANCED]:
    OnboardingStepAdvancedEventData,
  [ClientAnalyticsEventType.VIEW_DATA_OPENED]: ViewDataOpenedEventData,
  [ClientAnalyticsEventType.PROJECT_TEMPLATE_PREVIEWED]:
    ProjectTemplatePreviewedEventData,
  [ClientAnalyticsEventType.DEBUG_MODE_OPENED]: DebugModeOpenedEventData,
  [ClientAnalyticsEventType.NOTIFICATION_INBOX_OPENED]:
    NotificationInboxOpenedEventData,
  [ClientAnalyticsEventType.NOTIFICATION_CLICKED]: NotificationClickedEventData,
  [ClientAnalyticsEventType.KEYBOARD_SHORTCUT_USED]:
    KeyboardShortcutUsedEventData,
  [ClientAnalyticsEventType.PRESENTATION_MODE_OPENED]:
    PresentationModeOpenedEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_DIALOG_OPENED]:
    DataBrowserDialogOpenedEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_TABLE_SELECTED]:
    DataBrowserTableSelectedEventData,
  [ClientAnalyticsEventType.PUBLISH_MODAL_OPENED]: PublishModalOpenedEventData,
  [ClientAnalyticsEventType.CLIENT_COUNTER_EVENT]: ClientCounterEventData,
  [ClientAnalyticsEventType.PROJECT_LIST_LAYOUT_UPDATED]:
    ProjectListLayoutUpdatedEventData,
  [ClientAnalyticsEventType.PROJECT_LIST_SORT_UPDATED]:
    ProjectListSortUpdatedEventData,
  [ClientAnalyticsEventType.PROJECT_LIST_FILTER_UPDATED]:
    ProjectListFilterUpdatedEventData,
  [ClientAnalyticsEventType.EXPLORE_CREATED]: ExploreCreatedEventData,
  [ClientAnalyticsEventType.EXPLORE_ERRORED]: ExploreErroredEventData,
  [ClientAnalyticsEventType.SEMANTIC_DATASET_SELECTED]:
    SemanticDatasetSelectedEventData,
  [ClientAnalyticsEventType.CTA_CLICKED]: CtaClickedEventData,
  [ClientAnalyticsEventType.CHECKOUT_STEP_VIEWED]: CheckoutStepViewedEventData,
  [ClientAnalyticsEventType.CHECKOUT_PAYMENT_SUBMITTED]:
    CheckoutPaymentSubmittedEventData,
  [ClientAnalyticsEventType.CHECKOUT_DIALOG_DISMISSED]:
    CheckoutDialogDismissedEventData,
  [ClientAnalyticsEventType.CHECKOUT_SEAT_CHANGED]:
    CheckoutSeatChangedEventData,
  [ClientAnalyticsEventType.FEATURE_GATE_CONTACT_SALES_SUBMITTED]:
    FeatureGateContactSalesSubmittedEventData,
  [ClientAnalyticsEventType.CTA_CONTACT_SALES_SUBMITTED]:
    CtaContactSalesSubmittedEventData,
  [ClientAnalyticsEventType.CANCELLATION_STEP_ADVANCED]:
    CancellationStepAdvancedEventData,
  [ClientAnalyticsEventType.SEMANTIC_WORKBENCH_PUBLISH_TAB_CLICKED]:
    SemanticWorkbenchPublishTabClickedEventData,
  [ClientAnalyticsEventType.DRILLED_INTO_EXPLORE]: DrilledIntoExploreEventData,
  [ClientAnalyticsEventType.SEMANTIC_VIEW_SELECTED]:
    SemanticViewSelectedEventData,
  [ClientAnalyticsEventType.THREAD_VIEWED]: ThreadViewedEventData,
  [ClientAnalyticsEventType.THREAD_INSPECTOR_CLICK]:
    ThreadInspectorClickEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_SEARCH_RESULT_SELECTED]:
    DataBrowserSearchResultSelectedEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_SEARCH_TRIGGERED]:
    DataBrowserSearchTriggeredEventData,
  [ClientAnalyticsEventType.WORKSPACE_SEARCH_RESULT_CLICKED]:
    WorkspaceSearchResultClickedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_THREAD_OPENED]:
    ContextStudioThreadOpenedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_FILTER_CHANGED]:
    ContextStudioFilterChangedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_VIEWED]:
    ContextStudioViewedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_SUGGESTION_UI_ACTION]:
    ContextStudioSuggestionUiActionEventData,
  [ClientAnalyticsEventType.EXECUTION_PROFILER_OPENED]:
    ExecutionProfilerOpenedEventData,
  [ClientAnalyticsEventType.EXECUTION_PROFILER_TRACE_ID_COPIED]:
    ExecutionProfilerTraceIdCopiedEventData,
  [ClientAnalyticsEventType.AGENT_MODEL_EFFORT_SELECTED]:
    AgentModelEffortSelectedEventData,
  [ClientAnalyticsEventType.DATA_APP_SELECT_NUDGE]: DataAppSelectNudgeEventData,
} as const satisfies Record<ClientAnalyticsEventType, z.ZodTypeAny>;

type BatchClientAnalyticsEventMap = {
  [K in keyof typeof _eventTypeToData]: {
    event: K;
    properties: Requiredish<z.infer<(typeof _eventTypeToData)[K]>>;
  };
};
export type BatchClientAnalyticsEvent =
  BatchClientAnalyticsEventMap[keyof BatchClientAnalyticsEventMap];

type Distribute<T> = T extends { event: string; properties: any }
  ? [T["event"], T["properties"]]
  : never;
export type BatchClientAnalyticsTuples = Distribute<BatchClientAnalyticsEvent>;

/**
 * Browser-only page context sent from the client to the server's
 * `/track-page` endpoint. The server forwards these to Rudderstack's `page`
 * call, so the Rudderstack client SDK no longer needs to run in the browser.
 *
 * These mirror the standard page properties the Rudderstack JS SDK used to
 * auto-populate. All fields are strings (empty string when absent) so the
 * payload shape is stable regardless of the current location. The
 * `initial_*` fields capture the referrer of the first page in the session and
 * are persisted client-side, since that state previously lived in the SDK.
 */
export const ClientAnalyticsPageData = z.object({
  path: z.string(),
  url: z.string(),
  tab_url: z.string(),
  search: z.string(),
  title: z.string(),
  referrer: z.string(),
  referring_domain: z.string(),
  initial_referrer: z.string(),
  initial_referring_domain: z.string(),
});
export type ClientAnalyticsPageData = z.infer<typeof ClientAnalyticsPageData>;
